import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { LocationOn, OpenInNew } from '@mui/icons-material';

const hasLocation = (image) => (
  Number.isFinite(image?.location?.lat) &&
  Number.isFinite(image?.location?.lng) &&
  (image.location.lat !== 0 || image.location.lng !== 0)
);

const ImageLocationMap = ({ images, selectedImage }) => {
  const validImages = useMemo(() => images.filter(hasLocation), [images]);
  const [activeImage, setActiveImage] = useState(validImages[0] || null);

  useEffect(() => {
    if (hasLocation(selectedImage)) {
      setActiveImage(selectedImage);
    } else if (!hasLocation(activeImage)) {
      setActiveImage(validImages[0] || null);
    }
  }, [selectedImage, validImages, activeImage]);

  if (!activeImage) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', p: 3, bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center' }}>
          <LocationOn color="disabled" sx={{ fontSize: 40 }} />
          <Typography variant="h6">Location unavailable</Typography>
          <Typography variant="body2" color="text.secondary">
            No valid latitude and longitude were captured for these photos.
          </Typography>
        </Box>
      </Box>
    );
  }

  const { lat, lng } = activeImage.location;
  const offset = 0.006;
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${lng - offset}%2C${lat - offset}%2C${lng + offset}%2C${lat + offset}&layer=mapnik&marker=${lat}%2C${lng}`;
  const fullMapUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`;

  return (
    <Box sx={{ height: '100%', position: 'relative', bgcolor: 'background.default' }}>
      <Box
        component="iframe"
        title={`Location for ${activeImage.type || 'inspection photo'}`}
        src={mapUrl}
        loading="lazy"
        referrerPolicy="no-referrer"
        sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
      />
      <Button
        component="a"
        href={fullMapUrl}
        target="_blank"
        rel="noreferrer"
        variant="contained"
        size="small"
        startIcon={<OpenInNew />}
        sx={{
          position: 'absolute',
          left: 16,
          bottom: 16,
          zIndex: 3,
          boxShadow: 4,
          bgcolor: 'primary.main',
          '&:hover': { bgcolor: 'primary.dark' }
        }}
      >
        Open full map
      </Button>
    </Box>
  );
};

export default ImageLocationMap;
